import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders LLM-generated content as markdown — headings, lists,
 * tables, fenced code, blockquotes, links, etc. Used by every AI
 * answer surface (AgentsPage, BorderBuddyPage) so the output goes
 * from raw `## Heading\n**bold**` text to actually-formatted UI.
 *
 * Styling is inline rather than via @tailwindcss/typography because
 * we're not already pulling that plugin in. The element map below
 * approximates `prose` for the cases LLMs actually emit.
 *
 * Security: react-markdown is XSS-safe out of the box — it does
 * NOT render raw HTML. We don't add `rehype-raw`. URLs in `<a>` are
 * forced to open in a new tab with noopener.
 */
const COMPONENTS: Components = {
  // Headings — flatten by one level so an LLM-emitted "# Title"
  // doesn't dwarf the page chrome. Bold + tight tracking.
  h1: ({ children }) => <h2 className="text-base font-bold mt-4 mb-2 text-foreground">{children}</h2>,
  h2: ({ children }) => <h3 className="text-sm font-bold mt-4 mb-2 text-foreground">{children}</h3>,
  h3: ({ children }) => <h4 className="text-sm font-semibold mt-3 mb-1.5 text-foreground">{children}</h4>,
  h4: ({ children }) => <h5 className="text-xs font-semibold mt-3 mb-1 text-foreground uppercase tracking-wide">{children}</h5>,
  h5: ({ children }) => <h6 className="text-xs font-semibold mt-2 mb-1 text-foreground">{children}</h6>,
  h6: ({ children }) => <p className="text-xs font-semibold mt-2 mb-1 text-foreground">{children}</p>,

  p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,

  // Lists — tight enough that a 5-item list isn't a wall of air,
  // but loose enough that nested items breathe.
  ul: ({ children }) => <ul className="my-2 ms-5 list-disc space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="my-2 ms-5 list-decimal space-y-1">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,

  // Inline emphasis.
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through opacity-70">{children}</del>,

  // Code — inline pill + fenced block with subtle border.
  code: ({ children, className }) => {
    // remark-gfm tags fenced blocks with a language class; inline
    // code has no className. We use that to distinguish.
    const isBlock = className?.startsWith('language-');
    if (isBlock) {
      return <code className={`block ${className ?? ''}`}>{children}</code>;
    }
    return (
      <code className="rounded bg-muted px-1 py-0.5 text-[0.85em] font-mono text-foreground">{children}</code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-lg border border-border bg-muted/50 p-3 text-xs font-mono leading-relaxed text-foreground/90">
      {children}
    </pre>
  ),

  // Blockquote — left rail, muted ink. LLMs use these for "Note:" callouts.
  blockquote: ({ children }) => (
    <blockquote className="my-3 border-s-2 border-primary/50 bg-muted/30 px-3 py-2 text-foreground/85 italic">
      {children}
    </blockquote>
  ),

  // Tables — GFM. Compact, bordered.
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto">
      <table className="min-w-full border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
  th: ({ children }) => <th className="border border-border px-2 py-1.5 text-start font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-border px-2 py-1.5 align-top">{children}</td>,

  hr: () => <hr className="my-4 border-border" />,

  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2 hover:text-primary/80"
    >
      {children}
    </a>
  ),
};

export function MarkdownView({ children, className }: { children: string; className?: string }) {
  return (
    <div className={`markdown-view text-sm text-foreground/90 ${className ?? ''}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
